import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { downloadJobOfferDocument, downloadJobDescriptionDocument } from '../services/job.service';
import {
  getFileExtension,
  downloadBlob,
  handleDownloadError,
  createJobDocumentFilename,
} from '../utils/downloadUtils';
import { jobOfferPdfDownloadErrorToastKey } from '../utils/job-details-downloads';
import { JobOffer } from '../models/job';

/**
 * Custom hook for downloading job-related documents
 * Provides both public (candidate) and private (recruiter) download functionality
 */
export const useJobDownload = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation('job');

  const downloadJobOfferForCandidate = useCallback(
    async (job: JobOffer) => {
      try {
        const response = await downloadJobOfferDocument(job.id);

        if (!response.data || response.error) {
          toast.error(t(jobOfferPdfDownloadErrorToastKey(response.error)));
          return;
        }

        const blob = response.data;
        const extension = getFileExtension(blob.type);
        const filename = createJobDocumentFilename(
          job.title,
          job.companyName || 'company',
          'job_offer',
          extension,
        );

        downloadBlob(blob, filename, t('toasts.job_offer_pdf_download_success'));
      } catch (error) {
        const message = error instanceof Error ? error.message : undefined;
        toast.error(t(jobOfferPdfDownloadErrorToastKey(message)));
      }
    },
    [t],
  );

  const downloadJobDescriptionForRecruiter = useCallback(
    async (job: JobOffer) => {
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await downloadJobDescriptionDocument(accessToken, job.id);

        if (!response.data || response.error) {
          throw new Error(response.error || t('toasts.job_description_download_failed'));
        }

        const blob = response.data;
        const extension = getFileExtension(blob.type);
        const filename = createJobDocumentFilename(
          job.title,
          job.companyName || 'company',
          'job_description',
          extension,
        );

        downloadBlob(blob, filename, t('toasts.job_description_download_success'));
      } catch (error) {
        handleDownloadError(error, t('toasts.job_description_download_failed'));
      }
    },
    [getAccessTokenSilently, t],
  );

  return {
    downloadJobOfferForCandidate,
    downloadJobDescriptionForRecruiter,
  };
};
